import {LitElement, css, html,PropertyValues} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';

import './voyage-performance-chart';
import '../../voyage-spinner/src/voyage-spinner';
import { ParameterType, ParameterValue, VoyagePerformanceModel } from '../../../src/models/voyage-performance-model';
import { VesselsModel } from '../../../src/models/vessels-model';
import { VoyagePlansModel } from '../../../src/models/voyage-plans-model';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { VoyagePerformanceChart } from './voyage-performance-chart';
import { VoyagePlan } from '../../../src/models/vessel-planning-model';
import { PilotPointTimestamps } from '../../voy-timeseries-chart/src/echartUtils';
import { VoyTimeseriesChart } from '../../voy-timeseries-chart/src/voy-timeseries-chart';
import { PastTrailData, VoyagePastTrailModel } from '../../../src/models/voyage-past-trail-model';
import { getPilotPointIndeces } from '../../../src/utils/route-utils';

// Used in the pattern for each chart 
const scUrl = (<any>window)["appenv"].services.scfrontend.baseurl;

@customElement('voyage-performance-overview')
export class VoyagePerformanceOverview extends LitElement {

    @property({ type: Number })imo: number = 0;
    @property({ type: Boolean })hassc: boolean = false;
    @property({ type: Boolean })isShown: boolean = false;
    @property({attribute: false}) private _pilotPoints: PilotPointTimestamps | null = null;

    constructor() {
        super();

        this.updatePerformance = this.updatePerformance.bind(this);
        this._getPilotPointsTimestamps = this._getPilotPointsTimestamps.bind(this);
    }

    static get styles() {
        return [
            css`
            :host {
                display: flex;
                flex-direction: column;
                max-height: calc(100vh - 179px);
            }

            .list {
                min-height: 105px;
            }

            .chart-container {
                display: flex;
                flex-direction: column;
                flex-grow: 1;
                width: 390px;
                overflow-x: hidden;
                overflow-y: auto;
                max-height: calc(100vh - 277px);
            }

            .chart-container[maximized] {
                width: calc(100vw - 412px);
                max-height: calc(100vh - 220px);
            }

            .chart {
                flex-grow: 1;
            }

            .expand {
                position: absolute;
                top: 31px;
                right: 59px;
                fill: var(--neutrals-grey-02, black);

                cursor: pointer;
            }

            .expand > svg {
                display: block;
                height: 15px;
            }

        `];
    }

    render() {
        return html`
            <div class="list">
                <span
                    class="expand"
                    title="${this._maximized ? 'Minimize performance charts' : 'Maximize performance charts'}"
                    @click=${() => {this.setMaximized()}}>${this._maximized ? SvgIcons.solidCompressArrowsAlt : SvgIcons.expandPageIcon}
                </span>
                <div class="chart-container" ?maximized=${this._maximized}>
                    <voyage-performance-chart
                        class="chart"
                        id="sogchart"
                        imo=${this.imo}
                        ?hassc=${this.hassc}
                        tooltip="Click to see overview in Star Connect"
                        url="${scUrl}/vessel/overview/${this.imo}"
                        performanceChartTitle="Speed over ground"
                        unit="knots" 
                        .showSpinner=${this._dataIsFetching}
                        .pilotPoints=${this._pilotPoints}
                        >
                    </voyage-performance-chart>
                    <voyage-performance-chart
                        class="chart"
                        id="powerchart"
                        imo=${this.imo}
                        ?hassc=${this.hassc}
                        tooltip="Click to see propulsion details 14 days back in Star Connect"
                        url="${scUrl}/vessel/propulsion/${this.imo}"
                        performanceChartTitle="Propulsion ME power"
                        unit="kW"
                        .showSpinner=${this._dataIsFetching}
                        >
                    </voyage-performance-chart>
                    <voyage-performance-chart
                        class="chart"
                        id="rpmchart"
                        imo=${this.imo}
                        ?hassc=${this.hassc}
                        tooltip="Click to see propulsion trim/RPM details 7 days back in Star Connnect"
                        url="${scUrl}/vessel/propulsion/${this.imo}"
                        performanceChartTitle="Propulsion RPM"
                        unit="RPM"
                        .showSpinner=${this._dataIsFetching}
                        >
                    </voyage-performance-chart>
                </div>
            </div>
        `;
    }

    firstUpdated() {
        this._sogchart = this.shadowRoot!.querySelector('#sogchart');
        this._powerchart = this.shadowRoot!.querySelector('#powerchart');
        this._rpmchart = this.shadowRoot!.querySelector('#rpmchart');
        const imoUrl = UiStateModel!.getValue('imo');
        this.imo = imoUrl ? Number.parseInt(imoUrl) : 0;

        UiStateModel!.addEventListener(`${UiStateModelParam.IMO}-change`, <EventListener>((e: CustomEvent) => { this.imo = e.detail.value ? parseInt(e.detail.value) : 0; }));
        VoyagePerformanceModel!.addEventListener('fetch-data-started', () => this._dataIsFetching = true);
        VoyagePerformanceModel!.addEventListener('performance-updated', () => this.updateSimulatedData());
        VoyagePerformanceModel!.addEventListener('papi-performance-updated', () => this.updateActualData());
        VesselsModel!.addEventListener('vessels-loaded', () => this.updatePerformance());
        VoyagePlansModel!.addEventListener('plans-updated', this.updatePerformance);

        if (!this.isShown) return;

        if (!this.imo) {
            this.clear();
            return;
        }

        // initial load if a vessel is selected
        const scheduleDates = VoyageSchedulesModel!.getDatesFromSchedule(this.imo);
        VoyagePlansModel!.syncPlans(this.imo, scheduleDates.toDate);
    }

    private _getPilotPointsTimestamps(plan: VoyagePlan) {
        let departure = -1;
        let arrival = -1;
    
        if (plan.selectedRouteIndex === -1) {
            return { departure, arrival }
        }
    
        const route = plan.routes[plan.selectedRouteIndex];
    
        if (!route.route_info) {
            return { departure, arrival }
        }
    
        const ppIndeces = getPilotPointIndeces(route);
        if (ppIndeces.length === 0) {
            return { departure, arrival }
        }
    
        // If there is only one pilot point, it is assumed to be both departure and arrival pilot point (ie. there is nothing in between)
        if (ppIndeces.length > 1) {
            const depPilot = route.route_geometry[ppIndeces[0]];
            // Use ETA if present, otherwise use timestamps.
            departure = plan.departurePilotEtd > -1 ? plan.departurePilotEtd : new Date(depPilot.properties.timestamp).getTime();
        }
    
        const arrPilot = route.route_geometry[ppIndeces[ppIndeces.length-1]];
        // Use ETA if present, otherwise use timestamps.
        arrival = plan.arrivalPilotEta > -1 ? plan.arrivalPilotEta : new Date(arrPilot.properties.timestamp).getTime();

        this._pilotPoints = {
            departure,
            arrival
        };
    }

    private _convertPastTrailToParameterValues(pastTrail: PastTrailData[]): ParameterValue[] {
        const speedData: ParameterValue[] = []
        const timestampSet = new Set(); // setter to make sure there are no duplicates

        for (const pos of pastTrail) {
            if (!pos.speed) {
                continue;
            }

            const timestamp = Date.parse(pos.timestamp);

            if (timestampSet.has(timestamp)) {
                continue;
            }
            
            timestampSet.add(timestamp);

            speedData.push({
                timestamp: timestamp,
                value: pos.speed
            })
        }
        speedData.sort((a, b) => a.timestamp - b.timestamp);
        
        return speedData;
    }

    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((_, propName) => {
            if ((propName === "imo" || propName === "isShown") && this.isShown) {
                const vessel = VesselsModel!.getVessel(this.imo || 0);
                this.hassc = (vessel && vessel.hasPAPI) || false;

                if (!vessel) {
                    this.clear();
                    return;
                }

                const scheduleDates = VoyageSchedulesModel!.getDatesFromSchedule(this.imo);
                VoyagePlansModel!.syncPlans(this.imo, scheduleDates.toDate);
            }
        });
    }

    @property({attribute: false}) private _maximized: boolean = false;
    @property({attribute: false}) private _dataIsFetching = false;
    private _sogchart: VoyagePerformanceChart|null = null;
    private _powerchart: VoyagePerformanceChart|null = null;
    private _rpmchart: VoyagePerformanceChart|null = null;
    private _connected: boolean = false;

    async updateSimulatedData() {
        if (!this.isShown) return;

        try {
            this._sogchart!.simulated = VoyagePerformanceModel!.extractOneParam(ParameterType.SpeedOverGround, true);
            this._powerchart!.simulated = VoyagePerformanceModel!.extractOneParam(ParameterType.PropulsionPower, true);
            this._rpmchart!.simulated = VoyagePerformanceModel!.extractOneParam(ParameterType.RPM, true);
            if (!this._connected) {
                VoyTimeseriesChart.connect([this._sogchart!.chart!, this._powerchart!.chart!, this._rpmchart!.chart!]);
                this._connected = true;
            }
            this._dataIsFetching = false;
        } catch (err) {
            console.warn(err);
        }
    }
    
    async updateActualData() {
        if (!this.isShown) return;

        // In case there is a promise for past trail to finish loading,
        // wait for it to finish.
        if (VoyagePastTrailModel!.loadingPastTrailPromise) {
            await VoyagePastTrailModel!.loadingPastTrailPromise;
        }

        try {
            if (VoyagePastTrailModel!.trackSegment.length) {
                // Alarms API is using position API for actual speed. For consistency, also use 
                // positions API here (which is already present in past trail)
                this._sogchart!.actual = this._convertPastTrailToParameterValues(VoyagePastTrailModel!.trackSegment);
            } else {
                this._sogchart!.actual = [];
            }
            this._powerchart!.actual = VoyagePerformanceModel!.extractOnePAPIParam(ParameterType.PropulsionPower, true) || [];
            this._rpmchart!.actual = VoyagePerformanceModel!.extractOnePAPIParam(ParameterType.RPM, true) || [];
            this._dataIsFetching = false;
        } catch (err) {
            console.warn(err);
        }
    }

    setMaximized() {
        this._maximized = !this._maximized;
    }

    clear() {
        try {
            this._sogchart!.clear();
            this._powerchart!.clear();
            this._rpmchart!.clear();
        } catch (err) {
            console.warn(err);
        }
    }

    updatePerformance() {
        if (!this.isShown) return;
        
        if (!this.imo) {
            this.clear();
            return;
        }

        const scheduleDates = VoyageSchedulesModel!.getDatesFromSchedule(this.imo);
        const plan = VoyagePlansModel!.choosePlan(this.imo);
        VoyagePerformanceModel!.syncData(this.imo, scheduleDates.fromDate, scheduleDates.toDate, plan);

        if (plan) {
            this._getPilotPointsTimestamps(plan);
        }
    }
};
